/* storybook-check-ignore */
import { Modal } from '@opendoor/bricks/complex/Modal';
import { ModalProps } from 'components/shared/Modal';
import { Flex, Text, Button, Link } from '@opendoor/bricks/core';
import SimpleInfoIcon from './icons/SimpleInfoIcon';
import { ThemedLite } from '@opendoor/bricks/theme/ODThemeLite';
import { exclusives } from '@opendoor/bricks/theme';
import noop from 'lodash/noop';
import { ReactNode } from 'react';
import LetterIcon from './icons/LetterIcon';
import ArrowIcon from './icons/ArrowIcon';
import BackOutIcon from './icons/BackOutIcon';
import DiscountIcon from './icons/DiscountIcon';

const BENEFITS: BenefitProps[] = [
  {
    icon: <LetterIcon size={16} />,
    title: 'Appraisal Price Match Guarantee',
    details: (
      <>
        If the home appraises at a lower price, we'll match the appraised price up to $50,000.{' '}
        <Link
          aria-label={null}
          analyticsName=""
          href="/exclusives/faq?apmg=1"
          target="_blank"
          rel="noopener noreferrer"
        >
          Learn more
        </Link>
      </>
    ),
  },
  {
    icon: <ArrowIcon size={20} />,
    title: 'Purchase priority over other offers',
    details:
      'You don’t need to agonize about losing out to a higher bid. If you sign the contract first you are able to purchase the home at its current Exclusive Price',
  },
  {
    icon: <DiscountIcon size={16} />,
    title: 'Exclusive direct price',
    details:
      'We can save on traditional fees when we can sell a home directly to customers, those savings are reflected in the Exclusive price.',
  },
  {
    icon: <BackOutIcon size={20} />,
    title: 'Back out anytime, no penalty',
    details:
      'If you change your mind at any point before closing, you can cancel and receive a full refund of your earnest money deposit.',
    hideBorderBottom: true,
  },
];

export default function WhatIsODExclusivesModal(
  props: Pick<ModalProps, 'isOpen' | 'onRequestClose'>,
) {
  const icon = (
    <Flex
      ml={['0.5rem', '0.5rem', '1.5rem']}
      borderRadius="roundedSquare"
      height="72px"
      width="72px"
      bg="#EBEFFD"
      color="brand50"
      alignItems="center"
      justifyContent="center"
    >
      <SimpleInfoIcon size="40px" />
    </Flex>
  );
  return (
    <Modal
      aria-label="What is Opendoor Exclusives?"
      analyticsName=""
      {...props}
      desktopAndTabletSize="lg"
      headerProps={{
        leftComponent: icon,
      }}
    >
      <ThemedLite
        theme={{
          ...exclusives,
          breakpoints: exclusives.breakpoints,
        }}
      >
        <Flex flexDirection="column" gridGap="0.5rem" mx={['0.5rem', '0.5rem', '1.5rem']}>
          <Text fontWeight="semibold" fontSize="s2" color="brand50">
            What is Opendoor Exclusives?
          </Text>
          <Text color="neutrals90">
            With Opendoor Exclusives, we went back to the drawing board to design a purchasing
            experience that’s simple, certain, and streamlined and that puts you in control. We own
            this home and can sell it to you directly for less than its listed MLS price with
            additional benefits.
          </Text>
          <Flex flexDirection="column">{BENEFITS.map(Benefit)}</Flex>
          <Button
            as="a"
            variant="secondary"
            borderColor="brand50"
            color="brand50"
            _hover={{ opacity: 0.8, bg: 'neutrals30', color: 'brand50' }}
            aria-label={null}
            analyticsName=""
            href="/exclusives/faq"
            target="_blank"
            rel="noopener noreferrer"
            onClick={noop}
          >
            See our FAQ for more details
          </Button>
        </Flex>
      </ThemedLite>
    </Modal>
  );
}

type BenefitProps = {
  icon: ReactNode;
  title: string;
  details: ReactNode;
  hideBorderBottom?: boolean;
};
function Benefit({ icon, title, hideBorderBottom, details }: BenefitProps) {
  return (
    <Flex
      flexDirection="row"
      borderBottom={hideBorderBottom ? undefined : '1px solid rgba(0, 0, 0, 0.05)'}
      gridGap="1.5rem"
      py="1.5rem"
    >
      <Flex minHeight="20px" minWidth="20px">
        {icon}
      </Flex>
      <Flex flexDirection="column" gridGap="0.5rem">
        <Text fontWeight="semibold" fontSize="s1" textAlign="left" lineHeight="s000">
          {title}
        </Text>
        <Text opacity="0.8" textAlign="left" lineHeight="s00">
          {details}
        </Text>
      </Flex>
    </Flex>
  );
}
